import { createSelector } from "@reduxjs/toolkit";
import { pick } from "es-toolkit";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { usePrimaryLanguageCode, useSupportedLanguageCodes } from "../../l10n/index.ts";
import { appConfigSelector, getNextIdSelector } from "../../state/index.ts";
import { createNodeDataCreate, createPlaceholderDataCreate } from "../creation/index.ts";
import { migrateNodeCreate, type MigrateNode } from "./migrateNode.ts";

const manifestGettersSelector = createSelector(appConfigSelector, (appConfig) =>
  pick(appConfig, ["getManifest", "getPlaceholderManifest", "getMigrateFromManifest"]),
);

export const useMigrateNode = (): MigrateNode => {
  const supportedLanguageCodes = useSupportedLanguageCodes();
  const primaryLanguageCode = usePrimaryLanguageCode();
  const getNextId = useSelector(getNextIdSelector);
  const { getManifest, getMigrateFromManifest, getPlaceholderManifest } = useSelector(manifestGettersSelector);

  return useMemo(() => {
    const createNode = createNodeDataCreate({ getManifest, getPlaceholderManifest, getNextId });
    const createPlaceholder = createPlaceholderDataCreate(getPlaceholderManifest, createNode);
    return migrateNodeCreate({
      getManifest,
      getMigrateFromManifest,
      createNode,
      createPlaceholder,
      multilingual: {
        primaryLanguageCode,
        supportedLanguageCodes,
      },
    });
  }, [
    getManifest,
    getMigrateFromManifest,
    getNextId,
    getPlaceholderManifest,
    primaryLanguageCode,
    supportedLanguageCodes,
  ]);
};
